import push from "./assets/sounds/push.mp3";
import push2 from "./assets/sounds/push2.mp3";
import push3 from "./assets/sounds/push3.mp3";
import message from "./assets/sounds/message.mp3";
import assistant from "./assets/sounds/assistant_end.mp3";
import user from "./assets/sounds/user_end.mp3";
import speak from "./assets/sounds/speak.mp3";
import koka from "./assets/sounds/koka.mp3";
import kako from "./assets/sounds/kako.mp3";
import cancel from "./assets/sounds/cancel.mp3";
import cancel2 from "./assets/sounds/cancel2.mp3";
import alert from "./assets/sounds/alert.mp3";
import pifo from "./assets/sounds/pifo.mp3";
import on from "./assets/sounds/on.mp3";
import off from "./assets/sounds/off.mp3";

/**
 * 音声再生マネージャー
 * 現在再生中の音声を管理し、必要に応じて停止できるようにする
 */
class AudioManager {
  private currentAudio: HTMLAudioElement | null = null;
  // 再生世代トークン。新しい再生リクエストごとに加算し、合成完了までに
  // 画面遷移などで世代が変わった（古くなった）音声の再生を防ぐために使う。
  private playbackToken = 0;

  /**
   * 直近に発行された再生トークンを返す（合成開始時にキャプチャする用途）
   */
  get currentPlaybackToken(): number {
    return this.playbackToken;
  }

  /**
   * 新しい再生をリクエストする。
   * 現在再生中の音声を停止し、進行中（API合成待ち）のリクエストを無効化するため
   * 世代トークンを加算して、新しいトークンを返す。
   */
  requestPlayback(): number {
    this.stopCurrentAudio();
    return ++this.playbackToken;
  }

  /**
   * 指定トークンが最新の再生世代かどうかを返す。
   * false の場合、その音声は古い（別の再生に置き換わった）ため再生すべきではない。
   */
  isLatestPlayback(token: number): boolean {
    return token === this.playbackToken;
  }

  /**
   * 音声を登録する
   */
  setAudio(audio: HTMLAudioElement) {
    this.currentAudio = audio;
  }

  /**
   * 現在再生中の音声を停止する
   */
  stopCurrentAudio() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
  }

  /**
   * 音声再生が終了したらクリアする
   */
  clearAudio() {
    this.currentAudio = null;
  }
}

// グローバルインスタンスをエクスポート
export const audioManager = new AudioManager();

/**
 * 音声を安全に再生する関数
 * エラーハンドリング付きで、失敗時にはコンソールに警告を出力
 * @param audio 再生する音声要素
 * @returns 再生成功時はtrue、失敗時はfalse
 */
export const tryPlayAudio = async (audio: HTMLAudioElement): Promise<boolean> => {
  try {
    await audio.play();
    return true;
  } catch (error) {
    console.error("音声再生が失敗しました:", error);
    console.info(
      "ページをクリックしてから再度お試しください。 サイトの権限から音声を許可してください。"
    );
    window.alert("音声の再生に失敗しました。サイトの音声権限を許可にしてから再度お試しください。");
    return false;
  }
};

// TTS関連のインポート
import handleOpenAI from "./TTS/open_ai_tts/VoiceSynthesis";
import handleSynthesize from "./TTS/voicevox/VoiceSynthesis";
import handleAivisSpeechSynthesize from "./TTS/aivisspeech/VoiceSynthesis";
import { SpeakerInfo } from "./TTS/voicevox/VoicevoxUtils";
import { AivisSpeechSpeakerInfo } from "./TTS/aivisspeech/AivisSpeechUtils";
import { defaultSpeakerInfo, OpenAIInfo } from "./vrm_utils/VRMUtils";
import { t2SEngines } from "./Commons";

// 音声ファイルの定義
const sounds = {
  push: new Audio(push),
  push2: new Audio(push2),
  push3: new Audio(push3),
  message: new Audio(message),
  assistant: new Audio(assistant),
  user: new Audio(user),
  speak: new Audio(speak),
  koka: new Audio(koka),
  kako: new Audio(kako),
  cancel: new Audio(cancel),
  cancel2: new Audio(cancel2),
  alert: new Audio(alert),
  pifo: new Audio(pifo),
  on: new Audio(on),
  off: new Audio(off),
};

// 共通の再生関数
export const PlaySound = (soundKey: string, offsetVolume = 1.0) => {
  const sound = sounds[soundKey as keyof typeof sounds];
  try {
    if (sound) {
      sound.currentTime = 0;
      sound.volume = offsetVolume;
      tryPlayAudio(sound).then((success) => {
        if (!success) {
          console.info(`Failed to play sound: ${soundKey}`);
        }
      });
    } else {
      console.error(`Sound ${soundKey} not found`);
    }
  } catch (e) {
    console.error("Error trying to play audio:", e);
  }
};

/**
 * PlayVoice関数のパラメータ型
 */
export interface PlayVoiceParams {
  inputText: string;
  T2SEngine: string;
  currentSpeaker: {
    Id: number;
    Name: string;
    Style: string;
  };
  speed?: number;
  pitch?: number;
  intonation?: number;
  volume: number;
  talkingMode: number;
  setIsThinking?: (value: boolean) => void;
  setIsProcessing?: (value: boolean) => void;
  setIsTranscribing?: (value: boolean) => void;
  setOutputText?: (value: string) => void;
  setIsInConversationCycle?: (value: boolean) => void;
  endExpression?: string;
}

/**
 * テキストの音声再生関数
 */
export const playVoice = async (params: PlayVoiceParams) => {
  const {
    inputText,
    T2SEngine,
    currentSpeaker,
    speed,
    pitch,
    intonation,
    volume,
    talkingMode,
    setIsThinking,
    setIsProcessing,
    setOutputText,
    setIsTranscribing,
    setIsInConversationCycle,
    endExpression = "neutral",
  } = params;

  // 新しい音声を再生する前にすべての音声を停止し、新しい再生トークンを発行する。
  // 合成（API待ち）の途中で画面遷移などにより別の playVoice が呼ばれた場合、
  // 古い音声は再生せずに破棄するためにこのトークンを各ハンドラへ渡す。
  const playbackToken = audioManager.requestPlayback();
  // すべてのケース内で使用する可能性のある変数を外側で宣言
  let speakerInfo: SpeakerInfo;
  let openaiInfo;
  let aivisSpeechSpeakerInfo: AivisSpeechSpeakerInfo;

  // talkingModeが0以外の場合はsetOutputTextは呼び出さない
  const callSetOutputText = talkingMode === 0 ? setOutputText : () => {};

  switch (T2SEngine) {
    case t2SEngines.voicevox:
      speakerInfo = defaultSpeakerInfo;
      // 音声セット
      speakerInfo.id = [currentSpeaker.Id, currentSpeaker.Id];
      speakerInfo.query.speedScale = speed;
      speakerInfo.query.pitchScale = pitch;
      speakerInfo.query.intonationScale = intonation;
      speakerInfo.query.volumeScale = volume;

      await handleSynthesize(
        inputText,
        setIsThinking,
        setIsProcessing,
        setIsTranscribing,
        callSetOutputText,
        speakerInfo,
        setIsInConversationCycle,
        endExpression,
        playbackToken
      );
      // 発音リセット
      speakerInfo.query.speedScale = undefined;
      speakerInfo.query.pitchScale = undefined;
      speakerInfo.query.intonationScale = undefined;
      speakerInfo.query.volumeScale = undefined;
      break;

    case t2SEngines.openai:
      openaiInfo = OpenAIInfo;
      openaiInfo.Model = currentSpeaker.Style;
      openaiInfo.Voice = currentSpeaker.Name;
      await handleOpenAI(
        inputText,
        openaiInfo,
        volume,
        setIsThinking,
        setIsProcessing,
        setIsTranscribing,
        callSetOutputText,
        setIsInConversationCycle,
        endExpression,
        playbackToken
      );
      break;

    case t2SEngines.aivisSpeech:
      aivisSpeechSpeakerInfo = {
        id: [currentSpeaker.Id],
        query: {
          speedScale: speed,
          pitchScale: pitch,
          intonationScale: intonation,
          volumeScale: volume,
        },
      };
      await handleAivisSpeechSynthesize(
        inputText,
        setIsThinking,
        setIsProcessing,
        setIsTranscribing,
        callSetOutputText,
        aivisSpeechSpeakerInfo,
        setIsInConversationCycle,
        endExpression,
        playbackToken
      );
      // 発音リセット
      aivisSpeechSpeakerInfo.query = {
        speedScale: undefined,
        pitchScale: undefined,
        intonationScale: undefined,
        volumeScale: undefined,
      };
      break;
  }
};
